/**
 * The first steps of a new business, on its dashboard (QA-0006): an empty "Alles geprüft" told a
 * new owner nothing. Each step ticks itself off from the data; the card goes once all are done,
 * or when the owner hides it (remembered in this browser only).
 */
import { Check, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import type { BankAccount } from "@/lib/api/banking";
import type { DocumentRecord } from "@/lib/api/documents";
import type { Workspace } from "@/lib/api/workspaces";
import { cn } from "@/lib/utils/cn";

const STEP_KEYS = ["details", "receipt", "account", "inbox"] as const;
type Step = (typeof STEP_KEYS)[number];

const hiddenKey = (workspaceId: string) => `first-steps-hidden:${workspaceId}`;

function firstSteps(
  workspace: Pick<Workspace, "taxNumber" | "street" | "zip" | "city">,
  documents: Pick<DocumentRecord, "source">[],
  accounts: Pick<BankAccount, "id">[],
): Record<Step, boolean> {
  return {
    // The UVA needs the Steuernummer, an outgoing invoice the address.
    details: Boolean(workspace.taxNumber && workspace.street && workspace.zip && workspace.city),
    receipt: documents.length > 0,
    account: accounts.length > 0,
    inbox: documents.some((document) => document.source === "email"),
  };
}

export function FirstSteps({
  workspace,
  documents,
  accounts,
  onUpload,
}: {
  workspace: Workspace;
  documents: DocumentRecord[];
  accounts: BankAccount[];
  onUpload: () => void;
}) {
  const { t } = useTranslation();
  const [hidden, setHidden] = useState(() => {
    try {
      return localStorage.getItem(hiddenKey(workspace.id)) === "1";
    } catch {
      return false;
    }
  });
  const done = firstSteps(workspace, documents, accounts);
  if (hidden || STEP_KEYS.every((step) => done[step])) return null;

  const links: Record<Step, string | null> = {
    details: "/settings?tab=business",
    receipt: null,
    account: "/banking",
    inbox: "/settings?tab=inbox",
  };

  return (
    <section className="space-y-3 rounded-lg border bg-card p-6" data-testid="first-steps">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-medium">{t("firstSteps.title")}</h2>
          <p className="text-sm text-muted-foreground">{t("firstSteps.subtitle")}</p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t("firstSteps.hide")}
          onClick={() => {
            setHidden(true);
            try {
              localStorage.setItem(hiddenKey(workspace.id), "1");
            } catch {
              // Without storage the card simply comes back next time.
            }
          }}
        >
          <X className="size-4" />
        </Button>
      </div>
      <ol className="divide-y">
        {STEP_KEYS.map((step) => {
          const to = links[step];
          const label = t(`firstSteps.steps.${step}.title`);
          return (
            <li
              key={step}
              className="flex items-start gap-3 py-2.5"
              data-testid={`first-step-${step}`}
              data-done={done[step]}
            >
              <span
                className={cn(
                  "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border",
                  done[step] && "border-success bg-success text-success-foreground",
                )}
              >
                {done[step] && <Check className="size-3.5" />}
              </span>
              <div className="min-w-0 flex-1">
                <p className={cn("text-sm font-medium", done[step] && "text-muted-foreground")}>
                  {done[step] ? (
                    label
                  ) : to ? (
                    <Link
                      to={to}
                      className="text-primary underline decoration-primary/30 underline-offset-2 hover:decoration-primary"
                    >
                      {label}
                    </Link>
                  ) : (
                    <button
                      type="button"
                      onClick={onUpload}
                      className="text-primary underline decoration-primary/30 underline-offset-2 hover:decoration-primary"
                    >
                      {label}
                    </button>
                  )}
                </p>
                {!done[step] && (
                  <p className="text-xs text-muted-foreground">
                    {t(`firstSteps.steps.${step}.hint`)}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
